import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { addQuantity } from "~/features/addToCart/redux/cartAmounts/actions";
import {
  type AddToCartItem,
  type AddToCartPartItem,
  type AddToCartResponseItem,
  sendAddToCart,
} from "~/features/cart/cart/api/AddToCart";
import { zero, zeroNetPrice } from "~/utils/PriceUtils";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import type { RootState } from "~/app/store";
import { addMessageAdvanced } from "~/features/toasts/redux/actions";
import {
  getNetPrice,
  getPriceInfo,
  getSurcharge,
  getTariff,
  isProductPMA,
} from "~/domains/product/utils/productDescriptorUtils";
import type { AddToCartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import { userIsSalesman } from "~/domains/user/utils/roles";
import { sendAddToQuote } from "~/features/cart/activeQuote/api/addToQuote";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { getStore } from "~/app/store/getStore";
import { TranslationType } from "~/common/types/TranslationModel";
import { batch } from "react-redux";
import type { AddToCartSource } from "~/app/analytics/types";
import { AnalyticsMQ } from "~/app/analytics/AnalyticsMQ/AnalyticsMQ";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { isPhaseOut2Enabled } from "~/app/config/featureFlags";
import { Snackbar } from "~/common/mui";
import { SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { useTranslation } from "react-i18next";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

function prepareApiItemThunk(
  submitData: AddToCartSubmitData & { productCode: string; rdd?: Date; position?: number },
  isRestricted: boolean,
  context?: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
): ThunkResult<AddToCartItem> {
  return (dispatch, getState) => {
    const state = getState();
    const { formValues, derived } = submitData;
    const productCode = submitData.productCode;
    const priceReference = formValues.priceReference;

    const derivedDescriptor = derived.descriptor;
    const hasDerivedDescriptor = !!derivedDescriptor?.base?.code;
    const descriptor = hasDerivedDescriptor
      ? derivedDescriptor
      : selectProductDescriptor(state, {
          productCode,
          priceReference,
        });

    const baseInfo = descriptor.base;

    const packageCode = formValues.selectedPackCode;
    const selectedPackage = derived.selectedPack;
    const packageSize = derived.selectedPackaging?.packSize ?? selectedPackage?.packSize ?? 1;
    const totalQuantity = derived.totalQuantity;
    const selectPriceProps = { product: descriptor, packCode: packageCode, totalQuantity };
    const netPrice = derived.netPrice ?? getNetPrice(selectPriceProps) ?? zero();
    const surcharge = getSurcharge(selectPriceProps);
    const tariffSurchargeApplicable = getTariff(selectPriceProps.product);

    const priceInfo = getPriceInfo({ product: descriptor, packCode: packageCode });
    const is15DigitsPrice = descriptor.isMultiPrice;

    const optionalParts: AddToCartPartItem[] = Object.entries(submitData.optionalParts)
      .filter(([, part]) => part.formValues.selected)
      .map(([, part]) => {
        const partProductDescriptor = part.derived.descriptor;
        const packaging = ProductService.findPackagingByCode(partProductDescriptor, part.formValues.selectedPackCode);
        const fifteenDigit = partProductDescriptor.base.fifteenDigit[part.formValues.selectedPackCode];

        return {
          productCode: part.formValues.productId,
          packageCode: part.formValues.selectedPackCode,
          netPrice: part.derived.netPrice?.value ?? 0,
          priceUnit: part.derived.netPrice?.unit ?? 1,
          packageSize: packaging.packSize,
          quantity: part.formValues.quantity,
          manualUpdated: part.meta.isQtyUserModified,
          primaryWarehouseCode: packaging.primaryWarehouseCode,
          partTypeCode: ProductService.findOptionalPartById(descriptor, part.formValues.productId)!.type,
          surcharge: part.derived.netPrice?.surcharge ?? 0,
          tariffSurchargeApplicable: part.derived.netPrice?.tariffSurcharge,
          originCountry: fifteenDigit?.countryOfOrigin ?? "",
        };
      });

    const netPriceValue = netPrice?.value ?? 0;
    let salesNetPriceValue: number | undefined = undefined;
    let salesCampaignCode: string | undefined = undefined;
    let saleEnabled = false;
    if (formValues.salePriceEnabled) {
      const saleData = calculateSalesCampaign4PDP({ productDescriptor: descriptor, includeSoldOut: true });
      const saleForPack = saleData.data?.salePerPack[packageCode];
      if (saleForPack?.salesStock) {
        salesCampaignCode = saleForPack?.campaignCode;
        salesNetPriceValue = saleForPack?.actualPrice?.value;
        saleEnabled = true;
      }
    }

    const fifteenDigit = baseInfo.fifteenDigit[packageCode];

    const apiItem: AddToCartItem = {
      productCode: baseInfo.hybrisCode,
      packageSize,
      packageCode,
      quantity: formValues.quantity,
      comment: formValues.comment ?? "",
      netPrice: netPriceValue,
      priceUnit: priceInfo?.priceUnit ?? 1,
      selectedDeliveryDate: submitData.rdd ?? null,
      selectedDeliveryDateIsManual: !!submitData.rdd,
      primaryWarehouseCode: selectedPackage?.primaryWarehouseCode ?? "",
      fifteenDigit: !!is15DigitsPrice,
      priceReference: priceReference,
      isPMA: isProductPMA(descriptor),

      restrictedPackage: isRestricted,
      optionalParts,
      surcharge,
      position: submitData.position,
      tariffSurchargeApplicable,
      originCountry: fifteenDigit?.countryOfOrigin ?? "",
      salesCampaignCode: saleEnabled ? salesCampaignCode : undefined,
      saleNetPrice: saleEnabled ? salesNetPriceValue : undefined,
      specialPrice: saleEnabled,

      recommendationPlace: context?.recommendationPlace,
      recommendationType: context?.recommendationType,
    };

    return apiItem;
  };
}

function sendAddToCartRequestAction(
  apiItem: AddToCartItem | AddToCartItem[],
): ThunkResult<Promise<AddToCartResponseItem[] | undefined | null>> {
  return (dispatch, _getState) => {
    const items = Array.isArray(apiItem) ? apiItem : [apiItem];

    let promise: Promise<AddToCartResponseItem[] | undefined>;
    if (userIsSalesman()) {
      promise = sendAddToQuote(items.map((item) => ({ ...item, selectedDeliveryDate: new Date() }))).then(
        () => undefined,
      );
    } else {
      promise = sendAddToCart(items);
    }
    return promise
      .then((responseItems) => {
        batch(() => {
          items.forEach((item) => {
            dispatch(addQuantity(item.productCode, item.packageCode, item.quantity, item.priceReference));
          });
        });
        return responseItems;
      })
      .catch(() => {
        return null;
      });
  };
}

export async function bulkAddToCartApi({
  items,
  restrictionQuery,
  showSuccessDrawer = true,
  context,
}: {
  items: (AddToCartSubmitData & { productCode: string; rdd?: Date })[];
  restrictionQuery: string;
  showSuccessDrawer?: boolean;
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
}) {
  const dispatch = getStore().dispatch;

  const apiItems = items.map((item) => {
    const sapNo = ProductService.getBaseProductData(item.productCode)?.sapMaterialNumber;
    const isRestricted = !!(sapNo && restrictionQuery?.includes(sapNo) && isPackageRestrictionEnabled());

    const apiItem: AddToCartItem = dispatch(prepareApiItemThunk(item, isRestricted, context));
    return apiItem;
  });

  const isRecommendation = !!context.recommendationType && !!context.recommendationPlace;

  AnalyticsMQ.instance.push("addToCart", {
    products: items.map((item) => ({
      product: ProductService.getBaseProductData(item.productCode)!,
      packaging: {
        code: item.formValues.selectedPackCode,
        size: item.derived.selectedPackaging?.packSize ?? item.derived.selectedPack?.packSize ?? 1,
      },
      price: item.derived.netPrice ?? undefined,
      quantity: item.formValues.quantity,
    })),
    context: {
      source: "unknown",
      recommendationPlace: isRecommendation ? context.recommendationPlace : undefined,
      recommendationType: isRecommendation ? context.recommendationType : undefined,
    },
  });

  const responseItems = await dispatch(sendAddToCartRequestAction(apiItems));
  const result = responseItems !== null;

  if (result) {
    if (showSuccessDrawer) {
      const onWishlistPage = window.location.href.includes("wishlist");
      const phaseOut2Enabled = isPhaseOut2Enabled();

      // Make a proper fix in 6.5
      if (onWishlistPage && phaseOut2Enabled) {
        const reqAmount = apiItems.reduce((total, item) => total + item.quantity, 0);
        const resAmount: number = responseItems.reduce(
          (total: number, item: AddToCartResponseItem) => total + item.quantity,
          0,
        );
        const qtyAdjusted = reqAmount !== resAmount;

        if (qtyAdjusted) {
          openModal("qty-adjusted-toast", (close) => <QtyAdjustedSnackbar close={close} />);
        }
      }

      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal formData={items} onClose={close} page={context.page} responseItems={responseItems} />
      ));
    }
  } else {
    const totalQuantity = items.reduce((acc, item) => acc + item.formValues.quantity, 0);
    getStore().dispatch(
      addMessageAdvanced("error", {
        type: TranslationType.INTERNAL,
        value: "addToCart.failure",
        params: { count: totalQuantity },
      }),
    );
  }
  return result;
}

export async function addToCartApi({
  productCode,
  restrictionQuery,
  showSuccessDrawer = true,
  onSuccessCallback,
  context,
  submitData,
  addToCartSource,
  position,
}: {
  productCode: string;
  restrictionQuery: string;
  showSuccessDrawer?: boolean;
  position?: number;
  onSuccessCallback?: () => void;
  addToCartSource: AddToCartSource;
  submitData: AddToCartSubmitData;
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  };
}) {
  const product = ProductService.getBaseProductData(productCode)!;
  const sapNo = product.sapMaterialNumber;
  const isRestricted = !!(sapNo && restrictionQuery?.includes(sapNo) && isPackageRestrictionEnabled());

  const thunkData = { ...submitData, productCode, position };
  const apiItem: AddToCartItem = getStore().dispatch(prepareApiItemThunk(thunkData, isRestricted, context));

  const isRecommendation = !!context.recommendationType && !!context.recommendationPlace;

  AnalyticsMQ.instance.push("addToCart", {
    products: [
      {
        product,
        price: submitData.derived.netPrice ?? {
          ...zeroNetPrice(product),
        },
        quantity: apiItem.quantity,
        packaging: {
          code: apiItem.packageCode,
          size: apiItem.packageSize ?? 1,
        },
      },
    ],
    context: {
      source: addToCartSource,
      recommendationPlace: isRecommendation ? context.recommendationPlace : undefined,
      recommendationType: isRecommendation ? context.recommendationType : undefined,
    },
  });

  const responseItems = await getStore().dispatch(sendAddToCartRequestAction(apiItem));
  const result = responseItems !== null;

  if (result) {
    if (showSuccessDrawer) {
      openModal("add-to-cart-dialog", (close) => (
        <CartSuccessModal
          formData={[{ ...submitData, productCode }]}
          onClose={close}
          page={context.page}
          responseItems={responseItems}
        />
      ));
    }
    onSuccessCallback?.();
  } else {
    getStore().dispatch(
      addMessageAdvanced("error", {
        type: TranslationType.INTERNAL,
        value: "addToCart.failure",
        params: { count: submitData.formValues.quantity },
      }),
    );
  }
  return result;
}

const QtyAdjustedSnackbar = ({ close }: { close: () => void }) => {
  const { t } = useTranslation();

  return (
    <Snackbar
      open={true}
      message={t("product.phaseout.qtyAdjusted.past", "Your quantity has been adjusted")}
      onClose={close}
      action={<SnackbarCloseButton onClick={close} />}
    />
  );
};
